import React from 'react';
import { ProductsSection } from './orderForm/ProductsSection';
import {
  useProductSelection,
  type LineAddStartInfo,
  type LineAddedInfo,
} from '../hooks/useProductSelection';

// A phase card's body: the full ProductsSection (rich line rows — inline tonnage,
// extra-info, stock pills, qty/price editors — and the rich product picker),
// scoped to one section's lines. Each phase owns its own useProductSelection so
// the picker tags + tier-prices new lines on THIS section's window; rentals add
// straight into the phase.
//
// The ADD BEHAVIOR is NOT private to this component: the parent's shared
// optimistic lifecycle (onLineAddStart/onLineAdded/onLineAddFailed) is threaded
// straight through, so a phase add drops an instant placeholder and runs the
// single reconcile-after-line + refetch off the critical path — identical to an
// order-level or Unscheduled add. This per-section hook instance exists only
// because hooks can't be called per-section in a parent's render loop, NOT to
// give phases a different add path.

type ProductsProps = React.ComponentProps<typeof ProductsSection>;

interface PhaseProductsProps extends Omit<ProductsProps, 'onProductSelect'> {
  orderId: string;
  sectionId: string;
  /** Shared optimistic add lifecycle from the parent — same handlers every
   *  other add path uses. They tag/settle the placeholder by section. */
  onLineAddStart?: (info: LineAddStartInfo) => void;
  onLineAdded?: (info: LineAddedInfo) => void;
  onLineAddFailed?: (info: { tempId: string }) => void;
}

const PhaseProducts: React.FC<PhaseProductsProps> = ({
  orderId,
  sectionId,
  onLineAddStart,
  onLineAdded,
  onLineAddFailed,
  ...productProps
}) => {
  const { handleProductSelect, tierResolving } = useProductSelection({
    orderId,
    sectionId,
    onLineAddStart,
    onLineAdded,
    onLineAddFailed,
  });

  return (
    <ProductsSection
      {...productProps}
      // Every PhaseProducts render is a phase card, so its empty state reads
      // "This phase is empty" rather than "This order is empty".
      isPhase
      onProductSelect={handleProductSelect}
      // Disable the picker while this section's tier is still resolving so a
      // fast add can't land at $0.
      pricingResolving={productProps.pricingResolving || tierResolving}
    />
  );
};

export default PhaseProducts;
